import { useAsync } from "@amzn/stencil-react-components/hooks";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import React, { useContext } from "react";

import { Logger } from "@/apis/Logger";
import { ErrorLoadingFAQ } from "@/components/GetSupport/SelfHelpFAQ/FAQ/ErrorLoadingFAQ";
import { FAQFooter } from "@/components/GetSupport/SelfHelpFAQ/FAQ/Footer/FAQFooter";
import { FAQHeader } from "@/components/GetSupport/SelfHelpFAQ/FAQ/Header/FAQHeader";
import { FAQList } from "@/components/GetSupport/SelfHelpFAQ/FAQ/List/FAQList";
import { faqHelper, FAQResponse } from "@/helpers/faq-helper";
import { AppContext } from "@/stores/appStore";
import { useFeatureFlagsContext } from "@/stores/contexts/FeatureFlagsContext";

interface SelfHelpFAQContainerProps {
  onChatClick?: () => void;
}

/**
 * Main container for the FAQ section that manages data loading states and layout.
 *
 * @component
 * @param {Function} onChatClick - Optional callback for chat button interaction
 */

export const SelfHelpFAQContainer = ({
  onChatClick,
}: SelfHelpFAQContainerProps) => {
  const { state } = useContext(AppContext);
  const { featureFlags } = useFeatureFlagsContext();
  const { data, isLoading, error } = useAsync<FAQResponse>(
    async () => faqHelper.loadFAQData(state.currentStep.id),
    [state.currentStep.id]
  );

  const faqItems = (data?.faqItems ?? []).filter((item) => {
    if (
      item.requiresFeatureFlag &&
      !featureFlags?.[item.requiresFeatureFlag]?.enabled
    ) {
      return false;
    }
    if (
      item.hiddenByFeatureFlag &&
      featureFlags?.[item.hiddenByFeatureFlag]?.enabled
    ) {
      return false;
    }
    return true;
  });

  if (error) {
    void Logger.error(
      `Failed to load faq data for step ${state.currentStep.id} due ${error.message}`
    );
    return <ErrorLoadingFAQ />;
  }

  return (
    <View padding="S400">
      <Col gridGap="S400">
        {isLoading || !data ? (
          <Spinner />
        ) : (
          <>
            <FAQHeader title={data.faqTitle} />
            <FAQList items={faqItems} />
            <FAQFooter onChatClick={onChatClick} />
          </>
        )}
      </Col>
    </View>
  );
};
